// Browser DuckDB wrapper. Lazy-loads the wasm bundle on first use so
// pages that never hit "Run" don't pay the multi-MB download. Single
// shared connection persists across runs so tables created in one
// invocation are visible in the next (matches how a REPL session
// feels).
//
// Bundle source: jsDelivr. The DuckDB-WASM artefacts are 35–41 MB
// each, which exceeds Cloudflare Workers' 25 MiB per-asset cap, so
// we can't ship them in our own bundle. The same pattern is used by
// the bedevere-wise web app — see its DuckDBService.ts for the
// working reference.
//
// Single-threaded MVP variant is selected by `selectBundle` when
// SharedArrayBuffer isn't available; the threaded build needs
// COOP/COEP headers we don't currently set.

import * as duckdb from '@duckdb/duckdb-wasm';

export interface QueryResult {
  columns: string[];
  rows: unknown[][];
  /** Number of rows in the result. */
  rowCount: number;
}

let dbPromise: Promise<duckdb.AsyncDuckDB> | null = null;
let connPromise: Promise<duckdb.AsyncDuckDBConnection> | null = null;

async function instantiate(): Promise<duckdb.AsyncDuckDB> {
  const bundle = await duckdb.selectBundle(duckdb.getJsDelivrBundles());
  // Same-origin Blob shim — Workers can't be instantiated from a
  // cross-origin URL directly, but importScripts() inside a same-
  // origin worker can pull the cross-origin script.
  const workerUrl = URL.createObjectURL(
    new Blob([`importScripts("${bundle.mainWorker!}");`], { type: 'text/javascript' }),
  );
  const worker = new Worker(workerUrl);
  const logger = new duckdb.ConsoleLogger(duckdb.LogLevel.WARNING);
  const db = new duckdb.AsyncDuckDB(logger, worker);
  await db.instantiate(bundle.mainModule, bundle.pthreadWorker);
  URL.revokeObjectURL(workerUrl);
  await db.open({
    path: ':memory:',
    query: { castBigIntToDouble: true },
    // the-stats-duck is an unsigned local build; allow it.
    allowUnsignedExtensions: true,
  });
  return db;
}

export function getDuckDB(): Promise<duckdb.AsyncDuckDB> {
  if (!dbPromise) dbPromise = instantiate();
  return dbPromise;
}

export async function getConnection(): Promise<duckdb.AsyncDuckDBConnection> {
  if (!connPromise) {
    connPromise = (async () => {
      const db = await getDuckDB();
      const conn = await db.connect();
      // Mirror kolilang's native WORK schema setup so unqualified table
      // names resolve the same way both locally and in the browser.
      await conn.query('CREATE SCHEMA IF NOT EXISTS work');
      await conn.query("SET schema='work'");
      await tryLoadStatsDuck(conn);
      return conn;
    })();
  }
  return connPromise;
}

/// INSTALL + LOAD the-stats-duck against the published wasm
/// repository at `/extensions/stats-duck/v<version>/wasm_eh/`.
/// Failures are non-fatal — the SAS reader/writer paths and stats
/// PROCs simply error with a clearer "needs stats_duck" message
/// downstream when the function isn't in the catalog. Mirrors the
/// gating we already have on the native side.
async function tryLoadStatsDuck(conn: duckdb.AsyncDuckDBConnection): Promise<void> {
  const baseUrl = `${window.location.origin}${import.meta.env.BASE_URL}extensions/stats-duck`;
  try {
    await conn.query(`INSTALL stats_duck FROM '${baseUrl}'`);
    await conn.query('LOAD stats_duck');
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.log(`[kolilang] stats_duck loaded from ${baseUrl}`);
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn(
      `[kolilang] stats_duck not available — SAS readers / stats PROCs disabled. (${
        e instanceof Error ? e.message : String(e)
      })`,
    );
  }
}

export async function runQuery(sql: string): Promise<QueryResult> {
  const conn = await getConnection();
  const arrow = await conn.query(sql);
  const columns = arrow.schema.fields.map((f) => f.name);
  const rows = arrow.toArray().map((row) => columns.map((c) => row[c]));
  return { columns, rows, rowCount: rows.length };
}

/** Drop and recreate the session — used by a "Reset" button on the page. */
export async function resetSession(): Promise<void> {
  if (connPromise) {
    const conn = await connPromise;
    await conn.close();
  }
  if (dbPromise) {
    const db = await dbPromise;
    await db.terminate();
  }
  dbPromise = null;
  connPromise = null;
}
